import React, { Fragment, useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography
} from '@mui/material'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import CloseIcon from '@mui/icons-material/Close'
import { ProductoGrid, TypePackage } from '../entities/Product'
import { ICreateDun } from '../../../interfaces/StockKeepingUnitDun/StockKeepingUnitDun'
import { useApi } from '../../../hooks/useApi'
import { usePagination } from '../../../hooks/usePagination'
import { getListDunPage, getTypePackage } from '../api'
import { ApiResponse } from '../../../typings/ApiResponse'
import { GenericTable } from '../../../components/_EOS/Table'
import { Status } from '../../../hooks/useFetchState'
import { generateTableHeadersProduct } from '../Dun/config'

interface ViewProductoV2Props {
  elemento: ProductoGrid
  refreshGrid: () => void
}

interface BulletRowProps {
  label: string
  value: boolean | null | undefined
}

const BulletRow: React.FC<BulletRowProps> = ({ label, value }) => {
  const isTrue = value === true
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.4
      }}
    >
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Chip
        size='small'
        label={isTrue ? 'Sí' : 'No'}
        sx={{
          backgroundColor: isTrue ? '#4caf50' : '#e0e0e0',
          color: isTrue ? '#fff' : 'rgba(0,0,0,0.87)',
          fontWeight: 600,
          fontSize: '0.72rem',
          borderRadius: '20px',
          height: '22px',
          minWidth: '40px'
        }}
      />
    </Box>
  )
}

interface MeasureRowProps {
  label: string
  value: number | undefined
  unit: string
}

const MeasureRow: React.FC<MeasureRowProps> = ({ label, value, unit }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.3
    }}
  >
    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
      {label}:
    </Typography>
    <Typography variant='body2' sx={{ fontWeight: 600 }}>
      {value !== undefined ? `${value} ${unit}` : '—'}
    </Typography>
  </Box>
)

interface SectionHeaderProps {
  icon: React.ReactNode
  title: string
  color?: string
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  icon,
  title,
  color = '#ed6c02'
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
    <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
    <Typography
      variant='caption'
      sx={{ fontWeight: 700, color, letterSpacing: 1 }}
    >
      {title}
    </Typography>
  </Box>
)

export const ViewProductov2: React.FC<ViewProductoV2Props> = ({
  elemento
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false)

  const { state: stateTypePackage, callApi: callApiTypePackage } = useApi<TypePackage[]>()
  const { state: stateDuns, callApi: callApiListDun } = useApi<ApiResponse<ICreateDun>>()
  const pagination = usePagination(5)
  const tableHeadersProduct = generateTableHeadersProduct(stateTypePackage.data)

  const requestGetDuns = (page?: number, pageSize?: number) => {
    callApiListDun(getListDunPage(sku, page, pageSize))
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestGetDuns(auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  useEffect(() => {
    requestGetDuns(0, pagination.pageSize)
    callApiTypePackage(getTypePackage())
  }, [])
  const {
    name,
    sku,
    ean13,
    ERPCode,
    active,
    realHeight,
    realWidth,
    realLength,
    realWeight,
    shippingHeight,
    shippingWidth,
    shippingLength,
    shippingWeight,
    image,
    externalStorage,
    batch,
    expirationDate,
    isGreenSale
  } = elemento

  const imageUrl =
    image !== undefined && image.length > 0 ? image[0].url : null

  return (
    <Fragment>
      {/* ── HEADER ── */}
      <Paper
        variant='outlined'
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          mb: 1.5,
          borderRadius: 2
        }}
      >
        {/* Thumbnail */}
        <Box
          onClick={() => imageUrl && setLightboxOpen(true)}
          sx={{
            width: 56,
            height: 56,
            borderRadius: 1.5,
            overflow: 'hidden',
            flexShrink: 0,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            position: 'relative',
            cursor: imageUrl ? 'pointer' : 'default',
            '&:hover .zoom-overlay': {
              opacity: imageUrl ? 1 : 0
            }
          }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <BrokenImageOutlinedIcon sx={{ color: '#bdbdbd', fontSize: 36 }} />
          )}
          {/* Zoom overlay */}
          <Box
            className='zoom-overlay'
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s'
            }}
          >
            <ZoomInIcon sx={{ color: '#fff', fontSize: 32 }} />
          </Box>
        </Box>

        {/* Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant='subtitle1'
            sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}
            noWrap
          >
            {name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              <strong>SKU:</strong> {sku}
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              <strong>EAN:</strong> {ean13}
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              <strong>ERP Code:</strong> {ERPCode}
            </Typography>
            <Chip
              size='small'
              label={active ? 'Activo' : 'Inactivo'}
              sx={{
                backgroundColor: active ? '#4caf50' : '#f44336',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.72rem'
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* ── BODY ── */}
      <Grid container spacing={1.5}>
        {/* CARACTERÍSTICAS */}
        <Grid item xs={12} md={5}>
          <Card variant='outlined' sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <SectionHeader
                icon={<Inventory2OutlinedIcon fontSize='small' />}
                title='CARACTERÍSTICAS'
              />
              <Divider sx={{ mb: 0.5 }} />
              <BulletRow label='Almacenamiento externo' value={externalStorage} />
              <Divider />
              <BulletRow label='Contiene lote' value={batch} />
              <Divider />
              <BulletRow label='Contiene vencimiento' value={expirationDate} />
              <Divider />
              <BulletRow label='Venta en Verde' value={isGreenSale} />
              <Divider />
              <Box sx={{ height: 56 }} />
            </CardContent>
          </Card>
        </Grid>

        {/* MEDIDAS */}
        <Grid item xs={12} md={7}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, height: '100%' }}>
            {/* Medidas Producto */}
            <Card variant='outlined' sx={{ borderRadius: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <SectionHeader
                  icon={<StraightenOutlinedIcon fontSize='small' />}
                  title='MEDIDAS PRODUCTO'
                  color='#ed6c02'
                />
                <Divider sx={{ mb: 0.5 }} />
                <MeasureRow label='Alto' value={realHeight} unit='cm' />
                <MeasureRow label='Ancho' value={realWidth} unit='cm' />
                <MeasureRow label='Largo' value={realLength} unit='cm' />
                <MeasureRow label='Peso' value={realWeight} unit='kg' />
              </CardContent>
            </Card>

            {/* Medidas Envío */}
            <Card variant='outlined' sx={{ borderRadius: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <SectionHeader
                  icon={<LocalShippingOutlinedIcon fontSize='small' />}
                  title='MEDIDAS ENVÍO'
                  color='#1976d2'
                />
                <Divider sx={{ mb: 0.5 }} />
                <MeasureRow label='Alto' value={shippingHeight} unit='cm' />
                <MeasureRow label='Ancho' value={shippingWidth} unit='cm' />
                <MeasureRow label='Largo' value={shippingLength} unit='cm' />
                <MeasureRow label='Peso' value={shippingWeight} unit='kg' />
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
      {/* ── TABLA DUNs ── */}
      <Card variant='outlined' sx={{ borderRadius: 2, mt: 1.5 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <SectionHeader
            icon={<HubOutlinedIcon fontSize='small' />}
            title='DUN / EMPAQUES LOGÍSTICOS'
            color='#1976d2'
          />
          <Divider sx={{ mb: 0.5 }} />
          {stateDuns.status === Status.Loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : stateDuns.data?.items && stateDuns.data.items.length > 0 ? (
            <GenericTable<ICreateDun>
              dense={true}
              headersConfig={tableHeadersProduct}
              IDKey={'stockKeepingUnitDunId'}
              data={stateDuns.data.items}
              pagination={{
                totalItem: stateDuns.data.totalItems || 0,
                page: pagination.page,
                pageSize: pagination.pageSize
              }}
              onPaginationChange={handlePaginationChange}
              isLoading={false}
              showCheckboxes={false}
            />
          ) : (
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 3
              }}
            >
              <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                No existen registros de empaques logísticos para este producto
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Lightbox */}
      {imageUrl && (
        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth='md'
        >
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={() => setLightboxOpen(false)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: '#fff',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' }
              }}
              size='small'
            >
              <CloseIcon fontSize='small' />
            </IconButton>
            <img
              src={imageUrl}
              alt={name}
              style={{ display: 'block', maxWidth: '100%', maxHeight: '80vh' }}
            />
          </Box>
        </Dialog>
      )}
    </Fragment>
  )
}
